// The organisation's Setup (spec/13 §13.1, F-HQ-03, ADR-0047): `/o/{orgSlug}/setup`, where an owner lands until a venue
// opens. Its own steps first — a first venue (essential) and its business details (suggested: legal name and registered
// address, PAN optional; a venue needs them for GST tax invoices, not to open) — then a card per venue the member works
// at, with how many essentials it has done and the way into its own Setup.
// Creating a venue happens here, in the same centred dialog as the Venues page, and goes on to the new venue's Setup.
//
// Business details open in a centred dialog holding the same form as the organisation's settings (Money → Business
// details): an owner fills in the legal name, PAN and registered address right here, and the step reads the record's
// `complete` flag. Reading, changing and skipping them is `settings:all`; a member without it is shown which are on
// record — never the values — and that an owner changes them.

import type { OrganizationSetup, VenueSetup } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, ProgressBar, Skeleton, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../api/problems';
import { useAccess } from '../api/queries';
import { CreateVenueDialog } from '../org/venues/CreateVenueDialog';
import { BusinessDetailsForm, useBusinessDetails } from '../settings/BusinessDetailsGroup';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon, NavIcon } from '../shell/nav-icons';
import { useConsoleScope } from '../shell/scope';
import { CardsSkeleton, Facts, Forbidden, Notice, Page, Section } from '../ui';
import { useOrganizationSetup, useSkipOrganizationStep } from './queries';
import { SetupSteps } from './SetupSteps';
import { organizationStepAction, type SetupDialog } from './steps';

import './setup.css';
import { useProblem } from '../api/use-problem';

const VENUES_CREATE = 'venues:create';
/** The business details' own permission (ADR-0046): who may change them may skip them. */
const SETTINGS_ALL = 'settings:all';

export function OrganizationSetupPage(): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const scope = useConsoleScope();
  const organization = scope.organization;
  const organizationId = organization?.id ?? '';
  const access = useAccess(organizationId === '' ? undefined : { organizationId });
  const canCreate = access.status === 'ready' && access.can(VENUES_CREATE);
  const canSkip = access.status === 'ready' && access.can(SETTINGS_ALL);
  const setup = useOrganizationSetup(organizationId);
  const skip = useSkipOrganizationStep(organizationId);
  const { show } = useToast();
  const [dialog, setDialog] = useState<SetupDialog | null>(null);

  const title = t('setup.title');
  if (setup.data === undefined) {
    if (setup.isError && problemCode(setup.error) === PROBLEM.forbidden) {
      return (
        <Page title={title}>
          <Forbidden message={t('setup.notPermitted')} />
        </Page>
      );
    }
    return (
      <Page title={title} eyebrow={organization?.name}>
        {setup.isError ? <LoadFailure error={setup.error} message={t('setup.unavailable')} onRetry={() => void setup.refetch()} /> : <CardsSkeleton count={3} label={t('app.loading')} />}
      </Page>
    );
  }

  const data = setup.data;
  const create = canCreate ? (
    <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setDialog('createVenue')}>
      {t('setup.step.firstVenue.action')}
    </Button>
  ) : undefined;

  return (
    <Page title={title} eyebrow={organization?.name} description={t('setup.organizationIntro')}>
      <Section title={t('setup.businessTitle')} description={t('setup.businessHint')} body="rows">
        <SetupSteps
          steps={data.steps}
          label={t('setup.businessTitle')}
          actionFor={(step) => organizationStepAction(step.id)}
          // A member who may not create a venue is shown the step, not a button that would be refused.
          onDialog={(next) => (next === 'createVenue' && !canCreate ? undefined : setDialog(next))}
          busyStep={skip.isPending ? skip.variables?.step : undefined}
          onSkip={
            canSkip
              ? (step, skipIt) =>
                  skip.mutate(
                    { step: step.id as Parameters<typeof skip.mutate>[0]['step'], skip: skipIt },
                    { onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('setup.skipFailed') }) }) },
                  )
              : undefined
          }
        />
      </Section>

      <Section title={t('setup.venuesTitle')} description={t('setup.venuesHint')} actions={data.venues.length > 0 ? create : undefined}>
        {data.venues.length === 0 ? (
          <EmptyState icon={<NavIcon name="venues" />} title={t('setup.noVenuesTitle')} description={t('setup.noVenues')} action={create} />
        ) : (
          <ul className="console-setup-venues" aria-label={t('setup.venuesTitle')}>
            {data.venues.map((venue) => (
              <li key={venue.venueId}>
                <VenueSetupCard venue={venue} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {organizationId === '' ? null : <CreateVenueDialog organizationId={organizationId} open={dialog === 'createVenue'} onClose={() => setDialog(null)} />}
      {organizationId === '' ? null : (
        <BusinessDetailsDialog organizationId={organizationId} onRecord={data.businessDetails} open={dialog === 'businessDetails'} onClose={() => setDialog(null)} />
      )}
    </Page>
  );
}

/** A venue as its organisation's Setup shows it: where it stands, and the way into its own Setup. */
function VenueSetupCard({ venue }: { readonly venue: VenueSetup }): ReactNode {
  const { t } = useTranslation('console');
  const { progress } = venue;
  const opened = venue.status === 'live' || venue.status === 'paused';
  const ready = !opened && venue.missingEssentials.length === 0;
  return (
    <article className="console-setup-venue" aria-label={venue.name} data-ready={ready || undefined} data-open={opened || undefined}>
      <div className="console-setup-venue__head">
        <h3 className="console-setup-venue__name">{venue.name}</h3>
        {opened ? (
          <Badge tone={venue.status === 'live' ? 'ok' : 'warn'} dot>
            {venue.status === 'live' ? t('setup.venueLive') : t('setup.venuePaused')}
          </Badge>
        ) : ready ? (
          <Badge tone="ok">{t('setup.venueReady')}</Badge>
        ) : null}
      </div>
      <ProgressBar value={progress.essentialsTotal === 0 ? 1 : progress.essentialsDone / progress.essentialsTotal} label={t('setup.progressLabel')} tone="ok" size="sm" />
      <p className="console-subtle console-setup-venue__progress">
        {t('setup.progress', {
          essentialsDone: progress.essentialsDone,
          essentialsTotal: progress.essentialsTotal,
          suggestedSettled: progress.suggestedSettled,
          suggestedTotal: progress.suggestedTotal,
        })}
      </p>
      <Link className="clav-button clav-button--secondary clav-button--sm console-setup-venue__open" to="/v/$venueSlug/setup" params={{ venueSlug: venue.slug }} aria-label={t('setup.openVenue', { venue: venue.name })}>
        {t('setup.openVenueShort')}
      </Link>
    </article>
  );
}

/**
 * The organisation's business details, filled in here: the settings panel's form in a centred dialog. Its body mounts
 * only while open, so a member who may not read them is not asked until they open it.
 */
function BusinessDetailsDialog({
  organizationId,
  onRecord,
  open,
  onClose,
}: {
  readonly organizationId: string;
  readonly onRecord: OrganizationSetup['businessDetails'];
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      size="md"
      title={t('setup.business.title')}
      description={t('businessDetails.hint')}
      dirty="auto"
    >
      <BusinessDetailsBody organizationId={organizationId} onRecord={onRecord} onClose={onClose} />
    </Dialog>
  );
}

function BusinessDetailsBody({
  organizationId,
  onRecord,
  onClose,
}: {
  readonly organizationId: string;
  readonly onRecord: OrganizationSetup['businessDetails'];
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const details = useBusinessDetails(organizationId);
  if (details.data !== undefined) {
    return <BusinessDetailsForm key={JSON.stringify(details.data)} organizationId={organizationId} details={details.data} onSaved={onClose} onCancel={onClose} />;
  }
  if (details.isError && problemCode(details.error) === PROBLEM.forbidden) return <BusinessDetailsOnRecord onRecord={onRecord} onClose={onClose} />;
  if (details.isError) return <LoadFailure error={details.error} message={t('businessDetails.unavailable')} onRetry={() => void details.refetch()} />;
  return <Skeleton shape="text" lines={6} />;
}

/** For a member who may not read the business details: which are on record, never their values, and who changes them. */
function BusinessDetailsOnRecord({ onRecord, onClose }: { readonly onRecord: OrganizationSetup['businessDetails']; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const state = (recorded: boolean, optional = false) =>
    recorded ? (
      <Badge tone="ok" dot>
        {t('setup.business.onRecord')}
      </Badge>
    ) : (
      <Badge tone={optional ? 'neutral' : 'warn'} dot>
        {optional ? t('setup.business.optional') : t('setup.business.missing')}
      </Badge>
    );
  return (
    <div className="console-form">
      <Facts
        columns={1}
        items={[
          { key: 'legalName', label: t('setup.business.legalName'), value: state(onRecord.legalName) },
          { key: 'registeredAddress', label: t('setup.business.registeredAddress'), value: state(onRecord.registeredAddress) },
          { key: 'pan', label: t('setup.business.pan'), value: state(onRecord.pan, true) },
        ]}
      />
      <Notice role="note">{t('businessDetails.notPermitted')}</Notice>
      <div className="console-form__actions console-form__actions--end">
        <Button variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  );
}
